import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  cleanCV,
  cleanMsg,
  cleanState,
  confirmarComite,
  getCV,
  getComiteMiembrosPublico,
  removeUsuarioComite,
  useComite,
} from '../../../../../../appStore/comite';
import { useConcurso } from '../../../../../../providers/ConcursoProvider';
import AlertDialog from '../../../../../CompUtils/AlertDialog';
import { CommonRoot } from '../../../../../CompUtils/CommonRoot';
import { CommonTitle } from '../../../../../CompUtils/CommonTitle';
import { FileModal } from '../../../../../CompUtils/FileModal';
import FullLoading from '../../../../../CompUtils/FullLoading';
import { SuccessErrorDlg } from '../../../../../CompUtils/SuccessErrorDlg';
import { MiembroComiteType } from '../../../ConfeccionPrimerActa/GenerarAprobacion/ComiteMiembrosPopup';
import { fetchRolComitePublico, rolComitePublicoSelector } from '../../../gestionConcursoSlice';
import { ComiteHeader } from './ComiteHeader';
import { ComiteRow, RolComite } from './ComiteRow';
import { NuevoComiteMiembro } from './NuevoComiteMiembro';

const ConfirmDiv = styled.div`
  margin: 18px 10px;
`;

type Props = { esCC: boolean };
export const ComiteTable = (props: Props) => {
  const { esCC } = props;
  const miembroRef = useRef<MiembroComiteType | null>(null);
  const [actualRol, setActualRol] = useState<RolComite | null>(null);

  const [confirmation, setConfirmation] = useState(false);
  const [addDlg, setAddDlg] = useState(false);

  const { idLlamadoConcurso, comite } = useConcurso();
  const dispatch = useDispatch();

  const { loading, file, error, successAdd, success } = useComite();
  const roles = useSelector(rolComitePublicoSelector);
  const finalRoles = useMemo<RolComite[]>(() => Object.keys(roles).map((r) => roles[r]), [roles]);

  const renderComite = useCallback(() => {
    const rowMap = (r: any) => (
      <ComiteRow
        esCC={esCC}
        onRowClick={(action, miembro) => {
          switch (action) {
            case 'cv':
              if (miembro) dispatch(getCV(miembro));
              break;
            case 'delete':
              setConfirmation(true);
              miembroRef.current = miembro!!;
              break;
            case 'add':
              setAddDlg(true);
              setActualRol(r);
              break;
            default:
              break;
          }
        }}
        key={r.idRolComite}
        {...r}
      />
    );
    return (
      <>
        <ComiteHeader>Comité</ComiteHeader>
        {finalRoles.filter((e) => e.idRolComite < 5).map(rowMap)}
        <ComiteHeader>Coordinador Concursal</ComiteHeader>
        {finalRoles.filter((e) => e.idRolComite === 5).map(rowMap)}
        <ComiteHeader>Veedores</ComiteHeader>
        {finalRoles.filter((e) => e.idRolComite === 6).map(rowMap)}
      </>
    );
  }, [finalRoles, dispatch, esCC]);

  const handleAddClose = useCallback(() => {
    setAddDlg(false);
    setTimeout(() => {
      setActualRol(null);
    }, 500);
  }, []);

  const handleConfirm = useCallback(() => {
    const [first] = comite;
    const { idComite, idLlamadoConcurso } = first;
    dispatch(confirmarComite({ idComite, idLlamadoConcurso }));
  }, [dispatch, comite]);

  useEffect(() => {
    dispatch(getComiteMiembrosPublico({ idLlamadoConcurso }));
    dispatch(fetchRolComitePublico());
    return () => {
      dispatch(cleanState());
    };
  }, [dispatch, idLlamadoConcurso]);

  useEffect(() => {
    if (successAdd) {
      dispatch(cleanMsg());
      handleAddClose();
    }
  }, [dispatch, successAdd, handleAddClose]);

  return (
    <CommonRoot>
      <CommonTitle title="Miembros del Comité" subtitle="Listado de los miembros del comité" />
      <Table bordered hover>
        <thead>
          <tr className="text-center">
            <th>Rol</th>
            <th>Designación</th>
            <th>Nombre</th>
            <th>C.U.I.L.</th>
            <th>Correo</th>
            <th>Teléfono</th>
            <th>CV</th>
            {esCC && <th colSpan={2}>Acciones</th>}
          </tr>
        </thead>
        <tbody>{renderComite()}</tbody>
      </Table>
      {esCC && (
        <ConfirmDiv>
          <Button onClick={handleConfirm} variant="success">
            <i className="fa fa-check" />
            {` Guardar cambios`}
          </Button>
        </ConfirmDiv>
      )}
      <FileModal file={file} onClose={() => dispatch(cleanCV())} title="CV" />
      <AlertDialog
        btns={['accept']}
        icon="question"
        title="Atención"
        subtitle={`Se quitará del comité a '${miembroRef.current?.apellido}, ${miembroRef.current?.nombre} (${miembroRef.current?.cuil})'. ¿Desea continuar?`}
        show={confirmation}
        onClose={(wasAccepted) => {
          if (wasAccepted && miembroRef.current) {
            dispatch(removeUsuarioComite({ cuil: miembroRef.current.cuil }));
          }
          setConfirmation(false);
          setTimeout(() => {
            miembroRef.current = null;
          }, 500);
        }}
      />
      <AlertDialog
        show={addDlg}
        separator
        titleAlign="left"
        titleSize={'24px'}
        onClose={handleAddClose}
        title={`Ingresar ${actualRol?.nombre}`}
        btns={['close']}
        subtitle={
          actualRol ? <NuevoComiteMiembro onClose={handleAddClose} {...actualRol} /> : undefined
        }
      />
      <SuccessErrorDlg error={error} success={success} onClose={() => dispatch(cleanMsg())} />
      <FullLoading show={loading} />
    </CommonRoot>
  );
};
